import type { CouplingDrivenInput } from '@/domain/cosim/couplingDrivenInputs';

// Simulation 관련 TypeScript 타입 정의

/**
 * 통합 시뮬레이션 상태 (세션 레벨 + 모델 레벨 공용)
 * - 'building': Build 진행 중 (기존 'pending')
 * - 'resumed' 제거: 'running'으로 통합
 */
// terminated = 어댑터의 보고도 잡 래퍼가 종료 메시지에 남긴 판정도 없이 Job 이 끝났거나 사라져(SM 바깥에서 지움) SM 의 «죽은 Job»
// 회수가 판정 없이 정리한 참가자/실행 — 결과는 모른다(SM docs/shutdown-without-deadlines-design.md §9 — simulation-manager#230, 어휘는 정지 핸드셰이크
// 설계 결정 1).
// 결과 유무는 참가자 단위로 본다(결정 2) — 실행 배지가 terminated 여도 마무리한 참가자의 결과는 있다.
// unknown — 참가자의 상태를 모른다(어댑터가 응답하지 않음 등). 종결 판정이 아니다.
/** 실행·참가자 상태 어휘 — **이 목록이 정본이다**(타입도, 원문 값의 검사 집합도 여기서 끌어낸다) */
export const SIM_STATUSES = ['building', 'running', 'paused', 'completed', 'stopped', 'failed', 'terminated', 'unknown'] as const;
export type SimStatus = (typeof SIM_STATUSES)[number];
const SIM_STATUS_SET: ReadonlySet<string> = new Set<string>(SIM_STATUSES);
/** 원문(서버·저널의 소문자 글)이 이 어휘인가 */
export const isSimStatus = (raw: string | null | undefined): raw is SimStatus => raw != null && SIM_STATUS_SET.has(raw);
/** 진행 중 — 어댑터가 살아 값을 내는 상태. 값 조회(ICV·FMU 변수)는 세션과 그 참가자가 모두 이 상태일 때만 한다 */
export const LIVE_SIM_STATUSES: readonly SimStatus[] = ['running', 'paused'];
export const isLiveSimStatus = (status: SimStatus | null | undefined): boolean =>
  !!status && LIVE_SIM_STATUSES.includes(status);
/**
 * 참가자의 값을 조회·설정해도 되는가 — 서버에 이 세션의 실행이 있고(`runOnServer` — 데모 세션은 없다), 세션이 진행 중이고, 참가자가
 * 있다면 그 참가자도 진행 중이어야 한다. 참가자가 «상태 모름»(어댑터 무응답)이거나 먼저 끝났으면 세션이 진행 중이어도 조회하지 않는다.
 * 값 패널 셋(ICV GUI·ICV 목록·FMU 변수)이 이것 하나로 조회·컨트롤을 켜고, ICV 채널(`useICVPolling`)은 꺼져 있으면 쓰기도 보내지 않는다.
 * `sessionStatus` 는 화면이 세션 상태를 따로 받을 때(`simulationStatusOverride`)만 준다.
 */
export const valuesQueryable = (
  session: Pick<SimSession, 'demo' | 'status'> | null | undefined,
  participantStatus: SimStatus | null | undefined,
  sessionStatus: SimStatus | null | undefined = session?.status,
): boolean =>
  runOnServer(session) && isLiveSimStatus(sessionStatus ?? participantStatus) && (participantStatus == null || isLiveSimStatus(participantStatus));
/** 아직 끝나지 않은 실행 — 짓는 중이거나 진행 중. 이어보기의 대상 고르기·재접속 대조·실행 목록의 «실행 중» 카드가 읽는다 */
export const IN_PROGRESS_SIM_STATUSES: readonly SimStatus[] = ['building', ...LIVE_SIM_STATUSES];
export const isInProgressSimStatus = (status: SimStatus | null | undefined): boolean =>
  !!status && IN_PROGRESS_SIM_STATUSES.includes(status);
/** 종결 상태 — 세션 시계가 멈추고 참가자 endTime 이 확정되는 상태(타입 옆에 선언: 스토어·화면이 같은 것을 쓴다) */
export const TERMINAL_SIM_STATUSES: readonly SimStatus[] = ['completed', 'stopped', 'failed', 'terminated'];
/**
 * 정상 끝이 아닌 종결 — 참가자가 실패했거나(failed) 판정 없이 정리됐다(terminated). 사유가 기록되지 않았어도 화면은 늘 알린다
 * (사유가 없으면 없다고 말한다). 완주·정지는 사용자가 기대한 끝이라 사유가 있을 때만 말한다.
 */
export const ABNORMAL_END_STATUSES: readonly SimStatus[] = ['failed', 'terminated'];

/**
 * SimState 스트림 스냅샷 (BFF -> Web) — 공용 계약 vsmr.stream.v1.SimState
 */
/**
 * 실행이 도는 문제 시각 구간(초) — 진행률의 분모. 모르는 값은 undefined(날조하지 않는다):
 * 재시작의 시작은 rstplt 의 시각이라 웹이 모르고, 끝은 저작물(시간 단계·experiment·preCICE)에 없으면 모른다.
 */
export interface SimTimeWindow {
  start?: number;
  end?: number;
}

export interface SimStateSnapshot {
  task_id: string;
  task_type: string;
  seq: number;
  ts_ms: number;
  sim_time_ms: number;
  status: string;
  iteration: number;
  target_speed: number;
  actual_speed: number;
  max_speed: number;
}

/**
 * Telemetry 스트림 스냅샷 (BFF -> Web) — 공용 계약 vsmr.stream.v1.Telemetry.
 * 객체형 도메인(MARS volume/junction)은 그룹당 객체 하나, 평면형(FMU 변수
 * 나열)은 kind=''/objectId=0 그룹 하나.
 */
export interface TelemetryGroupSnapshot {
  kind: string;
  objectId: number;
  samples: Array<{ name: string; value: number; unit: string }>;
}

export interface TelemetrySnapshot {
  task_id: string;
  task_type: string;
  seq: number;
  ts_ms: number;
  sim_time_ms: number;
  groups: TelemetryGroupSnapshot[];
}

/**
 * 시뮬레이션 작업 모드
 */
export type TaskMode = 'new' | 'restart';

/**
 * 플롯 데이터 포인트
 */
export interface PlotData {
  time: number;                  // 시뮬레이션 시간
  [key: string]: number;         // 동적 센서 데이터 (p1, tempf1, mflowj1 등)
}

/**
 * MinorEdit 스냅샷 (이름 보존 버전 — Interactive Control 위젯 매핑용)
 */
export interface MinorEditNamedValue {
  name: string;   // 스트림 값 이름 — `카드번호:변수:파라미터` (domain/mars/stream/minorEditRequest)
  value: number;
}

export interface MinorEditNamedSnapshot {
  timehy: number;           // 시뮬레이션 시간 (ms)
  tsMs: number;             // wall-clock timestamp (ms)
  seq: number;
  values: MinorEditNamedValue[];
}

/**
 * 선 스타일 프리셋
 */
export type LineStylePreset = 'solid' | 'dotted' | 'dashed';

/**
 * 선 굵기 프리셋
 */
export type LineWidthPreset = 'thin' | 'normal' | 'bold';

/**
 * 개별 라인의 커스텀 스타일
 */
export interface LineStyle {
  color?: string;                  // hex 색상 (미지정 시 ChartConfig.dataKeys의 color 사용)
  stylePreset?: LineStylePreset;   // 선 스타일 (기본: 'solid')
  widthPreset?: LineWidthPreset;   // 선 굵기 (기본: 'normal')
}

/**
 * 차트 내 라인별 스타일 맵
 * key: dataKey (예: "p_10001", "tempf_20001")
 */
export type ChartLineStyles = Record<string, LineStyle>;

/**
 * 차트 크기 정의
 */
export type ChartSize = 'small' | 'medium' | 'large';

/**
 * 차트 레이아웃 정보 (react-grid-layout용)
 */
export interface ChartLayout {
  x: number;                      // 그리드 X 위치
  y: number;                      // 그리드 Y 위치
  w: number;                      // 그리드 너비 (칼럼 수)
  h: number;                      // 그리드 높이 (행 수)
  minW?: number;                  // 최소 너비
  minH?: number;                  // 최소 높이
  maxW?: number;                  // 최대 너비
  maxH?: number;                  // 최대 높이
}

/**
 * 차트 요약 정보
 */
export interface ChartSummary {
  chartId: string;                // 차트 ID
  currentValue: number | null;     // 현재값
  minValue: number | null;        // 최솟값
  maxValue: number | null;        // 최댓값
  sparklineData: Array<{          // 스파크라인 데이터 (최근 N개 포인트, 각 dataKey 포함)
    time: number;
    [key: string]: number;        // 각 dataKey의 값
  }>;
}

/**
 * 사용자 정의 탭 구성
 */
/**
 * 차트 구성
 */
export interface ChartConfig {
  id: string;                    // 차트 고유 ID
  title: string;                 // 차트 제목
  type: 'line' | 'area' | 'scatter' | 'heatmap' | 'gauge';
  sensors?: ChartSensor[];        // 표시할 센서 목록 (legacy)
  dataKeys?: Array<{              // 데이터 키 목록 (Minor Edit 기반)
    key: string;
    label: string;
    color: string;
  }>;
  unit?: string;                  // 단위 (예: 'Pa', 'K')
  yAxisDomain?: [number, number] | ['auto', 'auto']; // Y축 도메인
  yAxis?: {
    label: string;
    min?: number;
    max?: number;
  };
  xAxis?: {
    label: string;
    min?: number;
    max?: number;
  };
  // Minor Edit 기반 필드
  minorEditCardNumber?: number;   // cardRanges 'minor-edits/short'
  yAxisMode?: 'fixed' | 'auto';   // 사용자 토글 가능한 Y축 모드
  yAxisFixed?: [number, number];   // 고정 Y축 범위 [lowerLimit, upperLimit]
  editGroup?: number;              // 편집 그룹 번호 (탭 그룹화용)
  editPriority?: number;           // 그룹 내 우선순위
  // 레이아웃 관련 필드
  size?: ChartSize;                // 차트 크기 (small: 1/3폭, medium: 2/3폭, large: 패널 꽉 채움)
  tags?: string[];                 // 태그 (필터링용) — 카탈로그의 물리량 묶음 이름(예: '압력', '온도')
}

/**
 * 차트 센서 정의
 */
export interface ChartSensor {
  key: string;                   // 데이터 키 (예: 'p1', 'tempf1')
  label: string;                 // 표시 이름
  color?: string;                // 라인 색상
  unit?: string;                 // 단위 (예: 'Pa', 'K')
}

/**
 * 차트 그룹
 */
export interface ChartGroup {
  id: string;                    // 그룹 ID
  title: string;                 // 그룹 제목
  type: string;                  // 그룹 타입
  layout: 'primary' | 'secondary' | 'tertiary' | 'auxiliary';
  charts: ChartConfig[];         // 차트 목록
}

/**
 * 대시보드 레이아웃
 * Note: 기존 ChartLayout과 이름 충돌로 DashboardLayout으로 변경 (Line 38의 ChartLayout은 개별 차트 위치용)
 */
export interface DashboardLayout {
  grid: {
    columns: number;
    rows: number;
  };
  sections: ChartSection[];
}

/**
 * 차트 섹션
 */
export interface ChartSection {
  title: string;
  charts: ChartGroup[];
  span: {
    cols: number;
    rows: number;
  };
}

/**
 * 데이터 갭 정보
 */
export interface DataGap {
  start: number;                 // 시작 시퀀스
  end: number;                   // 종료 시퀀스
  missed: number;                // 누락된 데이터 수
  timestamp: number;             // 감지 시각
}

/**
 * 데이터 품질 지표
 */
export interface DataQuality {
  missedUpdates: number;         // 누락된 업데이트 수
  recoveryAttempts: number;      // 복구 시도 횟수
  lastGapTime: number | null;    // 마지막 갭 발생 시각
  isHealthy: boolean;            // 건강 상태
}

/**
 * 로그 수준
 */
export type LogLevel = 'debug' | 'info' | 'warning' | 'error';

/**
 * 로그 항목
 */
export interface LogEntry {
  timestamp: number;
  level: LogLevel;
  message: string;
  source?: string;
}

/**
 * 폴링 옵션
 */
export interface PollingOptions {
  interval?: number;             // 폴링 간격 (ms)
  maxRetries?: number;           // 최대 재시도 횟수
  timeout?: number;              // 타임아웃 (ms)
  onScreenLog?: (log: string) => void;
  onPlotLog?: (log: string) => void;
  onScreenComplete?: () => void;
  onPlotComplete?: () => void;
  onError?: (error: Error, type: 'screen' | 'plot') => void;
}

/**
 * 노드 그룹 분석 결과
 */
export interface NodeGroupAnalysis {
  volumes: NodeVolume[];
  junctions: NodeJunction[];
  heatStructures: NodeHeatStructure[];
  controllers: NodeController[];
  boundaries: NodeBoundary[];
}

/**
 * Volume 노드 정보
 */
export interface NodeVolume {
  id: number;
  name: string;
  type?: 'pipe';
  sensors: string[];
}

/**
 * Junction 노드 정보
 */
export interface NodeJunction {
  id: number;
  name: string;
  fromVolume?: number;
  toVolume?: number;
  sensors: string[];
}

/**
 * Heat Structure 노드 정보
 */
export interface NodeHeatStructure {
  id: number;
  name: string;
  sensors: string[];
}

/**
 * Controller 노드 정보
 */
export interface NodeController {
  id: number;
  name: string;
  type: string;
  sensors: string[];
}

/**
 * Boundary 노드 정보
 */
export interface NodeBoundary {
  id: number;
  name: string;
  type: string;
  sensors: string[];
}

/**
 * 연결 관계 분석
 */
export interface ConnectionAnalysis {
  loops: Loop[];
  branches: Branch[];
}

/**
 * 루프 정보
 */
export interface Loop {
  id: string;
  junctions: number[];
  volumes: number[];
}

/**
 * 분기 정보
 */
export interface Branch {
  id: string;
  fromJunction: number;
  toJunctions: number[];
}

// ============================================
// Co-Simulation 타입 정의
// ============================================

// SimulationStatus는 SimStatus로 통합됨 (하위 호환용 alias)
export type SimulationStatus = SimStatus;

/**
 * 모델별 데이터 (식별 + lifecycle + 실시간 스트리밍)
 */
export interface ModelSimData {
  // 식별
  modelId: string;
  modelName: string;
  taskId: string;
  taskIndex: number;
  /** participant 종류 ('mars' | 'fmu' | ...) — 스트림 소비·표시 스펙 선택 기준 */
  kind: string;
  /** 커플링이 구동하는 입력 — **시작 시점** 결선 스냅샷(#489). 부재 = 결선 없음(단독 실행) 또는
   *  미상(이어보기: 실행의 매니페스트를 되읽는 API 가 없다 — UI 제약 없음, 어댑터 거절만 남는다). */
  couplingDrivenInputs?: CouplingDrivenInput[];
  // lifecycle (기존 Job에서 통합)
  status: SimStatus;
  args: string;
  taskMode: TaskMode;
  startTime: number;
  endTime?: number;
  error?: string;
  lastSimState?: SimStateSnapshot;
  // streaming data
  plotData: PlotData[];
  /**
   * 이 모델이 플롯 포인트를 **한 번이라도** 받았는가 — 차트 그리드 지연
   * 마운트(chartsMounted)의 래치. "지금 있는가"로 게이트하면 재진입 비움
   * (resetModelStreamData)이 plotData 를 비우는 순간 그리드가 언마운트되고,
   * 완료된 세션에서는 영구히 사라진다. 모델 단위인 이유: co-sim 에서 한
   * 참가자의 첫 데이터가 다른 참가자의 그리드까지 마운트하면 지연 마운트의
   * 목적(첫 마운트 정지 회피)이 무너진다.
   */
  hadPlotData?: boolean;
  /** 스트림이 실어 온 변수 단위 (telemetry sample.unit) — 라이브 차트 라벨용 */
  variableUnits?: Record<string, string>;
  screenLogs: string[];
  /** 시나리오 적용 사건(«scenario» 채널) — 차트 세로 표식의 원천. 로그 창의 글은 screenLogs 에 함께 들어간다 */
  scenarioEvents?: import('@/domain/scenario').ScenarioLogEvent[];
  latestMinorEdit: MinorEditNamedSnapshot | null;
  runtimeMinorEdits: import('@/domain/mars/globals/minorEdits').MinorEdit[] | null;
}

/**
 * 시뮬레이션 세션 (단일/멀티 공통 — 단일 = 참가자 1명)
 */
export interface SimSession {
  simId: string;
  projectId: string;
  status: SimStatus;
  startTime: number;
  /** 이 실행의 문제 시각 구간 — 시작 때 확정해 실행 매니페스트(run.json)로 보존하고 이어보기가 되읽는다.
   *  없으면(구 실행·보존 실패) 모른다. 진행률은 이것만 읽는다 — 저장 모델을 다시 계산하지 않는다. */
  timeWindow?: SimTimeWindow;
  /** 이 실행이 고른 시나리오(id·이름) — 시작 경로는 붙인 결과, 이어보기는 실행 매니페스트에서. 없으면 시나리오 없는 실행 */
  scenario?: { id: string; name: string };
  models: Record<string, ModelSimData>;  // key = modelId
  /** 데모 세션 — 브라우저 안의 흉내라 서버(SM·BFF)에 이 실행이 없다. 소비자는 이 칸이 아니라 `runOnServer` 를 읽는다(D-170) */
  demo?: true;
}

/**
 * **이 세션의 실행이 서버(SM·BFF)에 있는가** — 데모 세션(`demo`)은 브라우저 안의 흉내라 없다. 서버를 부르는 소비자는 전부 이것만 읽는다:
 * 실행 제어(`useRunControls`), 스트림 구독(`SimulationPage`), 값 조회·설정(`valuesQueryable` — ICV·FMU 패널과 ICV 쓰기),
 * 분석 자동 적재(`analysisAutoLoadTaskId`), 표시 중인 실행(`useShownRun` — 결과 파일 메뉴·«결과 보기»), 세션 재조정(`sessionReconcile`).
 */
export function runOnServer(session: Pick<SimSession, 'demo'> | null | undefined): boolean {
  return !!session && session.demo !== true;
}
